import React, { useEffect, useState } from 'react';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

import HtmlPreview from './HtmlPreview';
import { CHANNEL_EMAIL, PreviewResponse } from './CommunicationTypes';
import type { ApiResult } from './MessageTemplateService';
import { previewTemplateVersion } from './MessageTemplateService';

export interface MessagePreviewProps {
  templateMasterUid: string;
  version: number;
  name: string;
  channel: number;
  onClose: () => void;
}

/**
 * One sentence per ApiResult variant — switched on `kind`, not probed for a `message` property.
 *
 * `('message' in res && res.message) || fallback` collapsed the two variants that carry NO message
 * — `forbidden` and `unauthorized` — into the same generic sentence, discarding the one distinction
 * the tagged-result design exists to preserve: a 403 means the server refused the call outright, a
 * 401 means sign in again, and neither is "the preview could not be loaded". The `&&`-then-`||`
 * chain also turned a legitimately empty `message: ''` into the fallback. Copy matches
 * ReviewDetail.runDecision and TemplateList.confirmPublish so the same failure reads the same way
 * across these screens.
 */
const describePreviewFailure = (res: Exclude<ApiResult<unknown>, { kind: 'ok' }>): string => {
  switch (res.kind) {
    case 'forbidden':
      return 'Refused — you do not have permission to preview this template.';
    case 'unauthorized':
      return 'Your session is not valid. Sign in again.';
    case 'notFound':
      return `${res.message} — this version may have been withdrawn.`;
    case 'rejected':
      return res.message;
    default:
      return res.message || 'The preview could not be loaded.';
  }
};

/**
 * The provider-side preview modal — mirrors the tenant's MessagePreview. Fetches the
 * server-rendered preview (sample-token substitution + EmailSanitizer) and shows it: email
 * bodies in a sandboxed HtmlPreview iframe, SMS as plain text. Read-only — opening this
 * authors/sends nothing.
 */
const MessagePreview: React.FC<MessagePreviewProps> = ({
  templateMasterUid, version, name, channel, onClose,
}) => {
  const { accessToken } = useOidcAccessToken();
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isEmail = channel === CHANNEL_EMAIL;

  useEffect(() => {
    let alive = true;
    setState('loading');
    previewTemplateVersion(templateMasterUid, version, accessToken).then((res) => {
      if (!alive) return;
      if (res.kind === 'ok') {
        setPreview(res.data);
        setState('loaded');
      } else {
        setError(describePreviewFailure(res));
        setState('error');
      }
    });
    return () => { alive = false; };
  }, [templateMasterUid, version, accessToken]);

  // Both channels get the same full-height sheet, matching the tenant's MessagePreview — preview
  // is one affordance, so it must not change size depending on which row's eye icon was clicked.
  //
  // The explicit Paper `height` is load-bearing, not decoration. MUI already makes Paper a flex
  // column and DialogContent `flex: 1 1 auto`, but a percentage/`100%` height (which HtmlPreview's
  // `fill` uses) resolves only against an ancestor chain of DEFINITE heights — with Paper
  // content-sized, `fill` collapses to zero. `maxHeight` alone is not a definite height.
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: 'calc(100vh - 64px)' } }}
    >
      <DialogTitle>
        Preview — {name} <Typography component="span" color="text.secondary">v{version}</Typography>
      </DialogTitle>
      {/* `display: flex` is required, not cosmetic. MUI gives DialogContent `flex: 1 1 auto` so it
          GROWS inside Paper, but it stays a block container — so `flex: 1` on the Stack below is
          inert and the body collapses to its content height, leaving the iframe short in a
          full-height dialog. Making this a flex column is what lets the chain reach the iframe. */}
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
        {state === 'loading' && (
          <Stack direction="row" spacing={1} alignItems="center" data-testid="preview-loading">
            <CircularProgress size={18} /> <Typography>Loading preview…</Typography>
          </Stack>
        )}
        {state === 'error' && (
          <Alert severity="error" data-testid="preview-error">{error}</Alert>
        )}
        {state === 'loaded' && preview && (
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            {isEmail && preview.subject != null && (
              <Box>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="subtitle1" data-testid="preview-subject">{preview.subject}</Typography>
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" color="text.secondary">Body</Typography>
              {isEmail ? (
                <HtmlPreview html={preview.renderedBody} testId="preview-body" fill />
              ) : (
                // Fills the same height as the email iframe, but caps the text at a reading width
                // and centres it — a ~160-character SMS set across a full-width sheet is
                // unreadable, and left-aligned it strands in the corner.
                <Box sx={{
                  flex: 1, minHeight: 0, overflowY: 'auto', p: 3,
                  border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  backgroundColor: 'background.default',
                }}
                >
                  <Box
                    data-testid="preview-body"
                    sx={{
                      whiteSpace: 'pre-wrap', maxWidth: 420, mx: 'auto',
                      fontSize: 14, lineHeight: 1.6,
                    }}
                  >
                    {preview.renderedBody}
                  </Box>
                </Box>
              )}
            </Box>
            {preview.segmentCountWarning && (
              <Alert severity="warning" data-testid="preview-segment-warning">
                {preview.segmentCountWarning}
              </Alert>
            )}
            {preview.emailViolations && preview.emailViolations.length > 0 && (
              <Alert severity="warning" data-testid="preview-email-violations">
                <Stack spacing={0.5}>
                  {preview.emailViolations.map((v) => (
                    <Typography key={v} variant="body2">{v}</Typography>
                  ))}
                </Stack>
              </Alert>
            )}
            {preview.unresolvedTokens && preview.unresolvedTokens.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Rendered with sample values for:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {preview.unresolvedTokens.map((t) => (
                    <Chip key={t} size="small" label={`{{${t}}}`} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="preview-close">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessagePreview;
